import { useIsomorphicEffect } from '@mantine/hooks'
import type { FC } from 'react'
import { useRef } from 'react'
import type {
  BlurhashDecodeRequest,
  BlurhashDecodeResponse,
} from 'src/workers/blurhash.worker'
import BlurhashWorker from 'src/workers/blurhash.worker?worker&inline'

const decodeSize = 32
let sharedWorker: Worker | null = null

const BlurHash: FC<
  {
    hash: string
  } & Omit<JSX.IntrinsicElements['canvas'], 'width' | 'height'>
> = ({ hash, ...props }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  /** useIsomorphicEffect for SSR compat; layout timing starts decoding as early as possible (~100ms gain) */
  useIsomorphicEffect(() => {
    if (typeof Worker === 'undefined') return

    const worker = sharedWorker ?? new BlurhashWorker()
    sharedWorker = worker

    const workerHandler = async ({
      data,
    }: MessageEvent<BlurhashDecodeResponse>) => {
      if (data.hash !== hash) return

      const ctx = canvasRef.current?.getContext('2d')
      if (!ctx) return

      const imageData = new ImageData(data.pixels, decodeSize, decodeSize)
      ctx.putImageData(imageData, 0, 0)
    }
    worker.addEventListener('message', workerHandler)

    worker.postMessage({ hash } satisfies BlurhashDecodeRequest)

    return () => {
      worker.removeEventListener('message', workerHandler)
    }
  }, [hash])

  return (
    <canvas ref={canvasRef} width={decodeSize} height={decodeSize} {...props} />
  )
}

export default BlurHash
